import { Faq, NavItem } from '../types/content'

export const navItems: NavItem[] = [
  { label: 'Shop', to: '/shop' },
  { label: 'How it works', to: '/how-it-works' },
  { label: 'Labs', to: '/labs' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'About', to: '/about' },
]

/** The four words printed on the inside of the box. */
export const principles = [
  {
    name: 'People',
    detail:
      'You get one clinician who knows your history — not whoever picks up the ticket that day.',
  },
  {
    name: 'Care',
    detail:
      'A real doctor reads your labs and writes your plan. Nothing ships because you clicked a button.',
  },
  {
    name: 'Progress',
    detail:
      'We test before, we test again after, and we tell you the truth when something is not working.',
  },
  {
    name: 'Together',
    detail:
      'Your labs and your plan belong to you. Download the lot and take it anywhere, any time.',
  },
]

export const trustMarks = [
  { label: 'US-licensed pharmacies', detail: 'Named on every order' },
  { label: 'Third-party tested', detail: 'Every batch, every lot' },
  { label: 'Unlimited doctor messaging', detail: 'Answered within a day' },
  { label: 'Free 2-day shipping', detail: 'Cold-packed, plain box' },
]

export const heroBenefits = [
  'Same price at any dose',
  'No multi-month contract',
  'Cancel in two clicks',
]

export const steps = [
  {
    label: 'Intake',
    title: 'Tell us what is going on',
    detail:
      'Ten minutes on your symptoms, your history and what you actually want to change. No card, no account.',
    duration: '10 minutes',
  },
  {
    label: 'Labs',
    title: 'Find out where you stand',
    detail:
      'Do it at home or stop by one of 2,100 draw sites. Your doctor walks you through the results in words you can use.',
    duration: '3–5 days',
  },
  {
    label: 'Plan',
    title: 'See the whole plan and the whole price',
    detail:
      'Medication, dose, pharmacy and monthly cost on one page. You say yes before we charge anything.',
    duration: '24–48 hours',
  },
  {
    label: 'Care',
    title: 'Adjust as you go',
    detail:
      'Retests, dose changes and messaging are part of your membership. Pause or cancel from your account.',
    duration: 'Ongoing',
  },
]

export const pricingTiers = [
  {
    name: 'Membership',
    price: 39,
    cadence: 'per month',
    description: 'Your doctor, your labs and your records. Medication billed separately.',
    features: [
      'Message your doctor as often as you need',
      'Baseline blood panel once a year',
      'Dose changes at no extra cost',
      'Download your records whenever',
    ],
    accent: false,
  },
  {
    name: 'Membership + treatment',
    price: 128,
    cadence: 'per month, from',
    description: 'Everything above, plus whatever your doctor prescribes you.',
    features: [
      'One active treatment',
      'Filled by US-licensed pharmacies',
      'Retested twice a year',
      'Free shipping, cold-packed',
      'Same price for a full year',
    ],
    accent: true,
  },
  {
    name: 'Labs only',
    price: 89,
    cadence: 'one time',
    description: 'For anyone who wants the numbers first and will decide later.',
    features: [
      '62-marker blood panel',
      'Written up by a doctor',
      'Optional call to talk it through',
      'No membership needed',
    ],
    accent: false,
  },
]

export const labPanels = [
  {
    name: 'Baseline',
    markers: 62,
    detail: 'Hormones, metabolic, thyroid, blood count and lipids. Where everyone starts.',
  },
  {
    name: 'Extended',
    markers: 104,
    detail: 'Adds ApoB, Lp(a), hs-CRP, homocysteine, IGF-1, insulin and micronutrients.',
  },
  {
    name: 'Retest',
    markers: 28,
    detail: 'The handful of markers that show whether your treatment is actually working.',
  },
]

export const faqs: Faq[] = [
  {
    question: 'Do I need a prescription?',
    answer:
      'Yes — everything here is prescription-only. Browse all of it with the prices in the open, then start your intake. A doctor licensed in your state decides what is right for you before anything gets filled.',
  },
  {
    question: 'Why can I not just add something to a cart?',
    answer:
      'Because this is medication, not a sweater. You tell us what you are interested in, your doctor looks at your labs and history, and you approve the plan in writing before a dollar moves.',
  },
  {
    question: 'Where does my medication come from?',
    answer:
      'US-licensed 503A and 503B pharmacies and standard wholesalers. Whoever filled your order is named on your plan and your paperwork, lot number included.',
  },
  {
    question: 'What am I paying for, exactly?',
    answer:
      'The medication, the supplies to use it, cold-packed shipping and any dose change your doctor makes. Membership is a separate $39 a month and covers your doctor and your labs.',
  },
  {
    question: 'Does my price go up when my dose goes up?',
    answer:
      'No. Your monthly price is locked for a year from the day you start, at any dose. If your doctor changes your plan, we tell you what it costs before anything changes.',
  },
  {
    question: 'Are compounded peptides FDA-approved?',
    answer:
      'No, and we say so on every one of them. Compounded medications are made by US-licensed pharmacies against your individual prescription. Semaglutide and Tirzepatide are the FDA-approved products here and are labelled that way.',
  },
  {
    question: 'What if I want to stop?',
    answer:
      'Cancel from your account — no retention call, no penalty. If stopping needs a taper, your doctor writes you one on the way out.',
  },
]

export const testimonials = [
  {
    quote:
      'I spent two years being told my labs were normal. Republic was the first place that actually read the whole panel back to me and explained it.',
    name: 'Marisa T.',
    detail: 'Member, 14 months',
  },
  {
    quote:
      'The plan came with the pharmacy, the lot number and the monthly cost on one page. I have never had that from a doctor before.',
    name: 'Daniel R.',
    detail: 'Member, 9 months',
  },
]
